import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { deleteToken } from "firebase/messaging";
import firebaseService from "@/firebaseConfig";

import { appLogoutApi } from "@/utils/apis/config";

import { useAppDispatch } from "@/store/hooks";
import { setFCMDeviceToken } from "@/store/slices/videoAppointment";
import { fetchHomeModules } from "@/store/slices/patient/home";
import { persistor } from "@/store/slices";

import { IResetAppState, ISessionKeys } from "@/models";

import { storage } from "@/utils/Storage";
import { notify, INotifyEnum } from "@/utils/toaster";

import useIsMobile from "./useIsMobile";
import useIsTablet from "./useIsTablet";

const useLogout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();

  const cleanupAudio = () => {
    const audioElements = document.getElementsByTagName("audio");
    for (let i = 0; i < audioElements.length; i++) {
      audioElements[i].pause();
      audioElements[i].currentTime = 0;
    }
  };

  const logoutOnExpire = async () => {
    if (isMobile || isTablet) {
      navigate("/");
    } else {
      navigate("/home");
    }

    dispatch({ type: IResetAppState.RESET_ALL });
    // 3. Purge persisted data from storage
    try {
      await persistor.purge();
      console.log("Redux state and persisted data purged successfully.");
    } catch (error) {
      console.error("Error purging persisted data:", error);
    }

    localStorage.clear();
    await deleteToken(firebaseService.messaging);
    cleanupAudio();
    const userId = storage.getSessionToken(ISessionKeys.USER_ID);
    const deviceToken = await firebaseService.generateTokenFCM();
    dispatch(setFCMDeviceToken(deviceToken));
    await dispatch(fetchHomeModules({ userId: userId! }));
  };

  const logout = useCallback(async () => {
    try {
      const appLogoutResponse = await appLogoutApi();
      if ("result" in appLogoutResponse) {
        cleanupAudio();
        await logoutOnExpire();
      } else {
        notify(INotifyEnum.ERROR, appLogoutResponse?.message);
      }
    } catch (error) {
      console.error("Logout Error:", error);
    }
  }, []);

  return { logout, logoutOnExpire };
};

export default useLogout;
